import { StateCreator, create } from 'zustand'
import { ToasterType } from 'Types/ToasterTypes'
import { nanoid } from 'nanoid'
import { TransConfig } from 'Types/i18n'
import { devtools } from 'zustand/middleware'
import { routesNames } from 'Consts/routeNames'
import { Domain } from 'Types/Domain'

export type RoutesNamesType = keyof typeof routesNames | false

export const TOASTS_TYPES = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  INFO: 'info',
  WARNING: 'warning',
} as const

export interface PmPlatformConnectionModalStatusType {
  isOpen: boolean
  platform: string
  connectionId?: number
}

export interface ToastType {
  text: string
  type: ToasterType
  id: string
}

export interface SystemMessageType {
  i18config: TransConfig
  type: ToasterType
  id: string
  callbackOnClose?: () => void
}

export interface AppStore {
  isAuthenticating: boolean
  isMobileMenuOpen: boolean
  isMenuCollapsed: boolean
  isOnboardingModalOpen: boolean
  pmPlatformConnectionModalStatus: PmPlatformConnectionModalStatusType
  redirectRoute: RoutesNamesType
  toggleMobileMenu: (toggle: boolean) => void
  showToaster: (text: string, type: ToasterType) => void
  removeToast: (id: string) => void
  toasts: ToastType[]
  showSystemMessage: (
    i18config: TransConfig,
    type: ToasterType,
    callbackOnClose?: () => void
  ) => void
  removeSystemMessage: (id?: string) => void
  systemMessages: SystemMessageType[]
  modalToShow: string
  modalToShowData?: []
  disablePlatformConnectionModal: boolean
  setModalToShow: (name: string, data?: Array<string | number | null>) => void
  currentDomain?: Domain
  setCurrentDomain: (domain?: Domain) => void
  setPmPlatformConnectionModal: (toggle: boolean, platform: string, id?: number) => void
  toggleMenuCollapsed: (toggle: boolean) => void
  setOnboardingModal: (status: boolean) => void
  setRedirectRoute: (status: RoutesNamesType) => void
  setIsAuthenticating: (status: boolean) => void
  setShowCheckoutSuccess: (show: boolean) => void
  showCheckoutSuccess: boolean
  setNoCcSaveSubscriptionInProgress: (show: boolean) => void
  noCcSaveSubscriptionInProgress: boolean
  setDisablePlatformConnectionModal: (status: boolean) => void
}

const appStore = (set: (fn: (state: AppStore) => AppStore) => void) => ({
  isAuthenticating: false,
  modalToShow: '',
  isMobileMenuOpen: false,
  isOnboardingModalOpen: false,
  redirectRoute: false,
  showCheckoutSuccess: false,
  noCcSaveSubscriptionInProgress: false,
  disablePlatformConnectionModal: false,
  pmPlatformConnectionModalStatus: {
    isOpen: false,
    platform: '',
  },
  isMenuCollapsed: false,
  toasts: [],
  systemMessages: [],
  currentDomain: undefined,
  toggleMobileMenu: (toggle: boolean) => {
    set((state: AppStore) => ({
      ...state,
      isMobileMenuOpen: toggle,
    }))
  },
  setModalToShow: (name: string, data: [] = []) => {
    set((state: AppStore) => ({
      ...state,
      modalToShow: name,
      modalToShowData: data,
    }))
  },
  setCurrentDomain: (domain?: Domain) => {
    set((state: AppStore) => ({
      ...state,
      currentDomain: domain,
    }))
  },
  setPmPlatformConnectionModal: (toggle: boolean, platform: string, id?: number) => {
    set((state: AppStore) => ({
      ...state,
      pmPlatformConnectionModalStatus: { platform, isOpen: toggle, id },
    }))
  },
  toggleMenuCollapsed: (toggle: boolean) => {
    set((state: AppStore) => ({
      ...state,
      isMenuCollapsed: toggle,
    }))
  },
  setOnboardingModal: (status: boolean) => {
    set((state: AppStore) => ({
      ...state,
      isOnboardingModalOpen: status,
    }))
  },
  setRedirectRoute: (routeName: RoutesNamesType) => {
    set((state: AppStore) => ({
      ...state,
      redirectRoute: routeName,
    }))
  },
  showToaster: (text: string, type: ToasterType) => {
    set((state: AppStore) => ({
      ...state,
      toasts: [...state.toasts, { text, type, id: nanoid() }],
    }))
  },
  removeToast: (id: string) => {
    set((state: AppStore) => ({
      ...state,
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }))
  },
  showSystemMessage: (i18config: TransConfig, type: ToasterType, callbackOnClose?: () => void) => {
    set((state: AppStore) => ({
      ...state,
      systemMessages: [...state.systemMessages, { i18config, type, id: nanoid(), callbackOnClose }],
    }))
  },
  removeSystemMessage: (id?: string) => {
    set((state: AppStore) => ({
      ...state,
      systemMessages: id ? state.systemMessages.filter((toast) => toast.id !== id) : [],
    }))
  },
  setIsAuthenticating: (status: boolean) => {
    set((state: AppStore) => ({
      ...state,
      isAuthenticating: status,
    }))
  },
  setShowCheckoutSuccess: (show: boolean) => {
    set((state: AppStore) => ({
      ...state,
      showCheckoutSuccess: show,
    }))
  },
  setNoCcSaveSubscriptionInProgress: (show: boolean) => {
    set((state: AppStore) => ({
      ...state,
      noCcSaveSubscriptionInProgress: show,
    }))
  },
  setDisablePlatformConnectionModal: (status: boolean) => {
    set((state: AppStore) => ({
      ...state,
      disablePlatformConnectionModal: status,
    }))
  },
})

const useAppStore = create<AppStore>()(
  devtools(appStore as StateCreator<AppStore, [['zustand/devtools', never]], [], AppStore>)
)
export default useAppStore
